import { useCallback, useMemo, useState } from 'react';
import { useGridFilter } from 'ag-grid-react';

import { useAgConditionModel } from './useAgConditionModel';
import { useSubmitAgModel } from './useSubmitAgModel';

import useFEGridConfig from './useFEGridConfig';

import { UseAgDateFilterCoreProps } from '../types';

const useAgDateFilterCore = ({
  model,
  defaultOption,
  field,
  isBEGrid = true,
  onModelChange,
  filterValueTransform,
}: UseAgDateFilterCoreProps) => {
  const [operator, setOperator] = useState<'or' | 'and'>('or');
  const [closeFilter, setCloseFilter] = useState();

  const afterGuiAttached = useCallback(({ hidePopup }) => {
    setCloseFilter(() => hidePopup);
  }, []);

  const { primaryConditionData, secondaryConditionData } = useMemo(() => {
    if (model?.operator) {
      const [p, s] = model.conditions;
      return {
        primaryConditionData: {
          defaultFromDate: p.dateFrom,
          defaultToDate: p.dateTo,
          defaultCondition: p.type || defaultOption,
        },
        secondaryConditionData: {
          defaultFromDate: s.dateFrom,
          defaultToDate: s.dateTo,
          defaultCondition: s.type || defaultOption,
        },
      };
    }

    return {
      primaryConditionData: {
        defaultFromDate: model?.dateFrom,
        defaultToDate: model?.dateTo,
        defaultCondition: model?.type || defaultOption,
      },
      secondaryConditionData: {
        defaultFromDate: null,
        defaultToDate: null,
        defaultCondition: defaultOption,
      },
    };
  }, [defaultOption, model]);

  const primaryCondition = useAgConditionModel(primaryConditionData);
  const secondaryCondition = useAgConditionModel(secondaryConditionData);
  const isRangeError = primaryCondition.isRangeError || secondaryCondition.isRangeError;

  const resetFilterData = useCallback(() => {
    primaryCondition.resetToDefault();
    secondaryCondition.resetToDefault();
  }, [primaryCondition, secondaryCondition]);

  const modelSubmitData = useSubmitAgModel({ conditions: [primaryCondition, secondaryCondition], operator });

  const { isFilterActive, doesFilterPass, setConfirmedPrimary, setConfirmedSecondary, setConfirmedOperator } =
    useFEGridConfig({ primaryCondition, secondaryCondition, operator, field, filterValueTransform });

  useGridFilter({
    afterGuiAttached,
    afterGuiDetached: resetFilterData,
    ...(isBEGrid ? {} : { isFilterActive, doesFilterPass }),
  });

  const handleAccept = useCallback(() => {
    if (primaryCondition.isError || isRangeError) return;
    if (!isBEGrid) {
      setConfirmedPrimary({ ...primaryCondition });
      setConfirmedSecondary({ ...secondaryCondition });
      setConfirmedOperator(operator);
    }
    onModelChange(modelSubmitData);

    closeFilter?.();
  }, [
    closeFilter,
    isBEGrid,
    isRangeError,
    modelSubmitData,
    onModelChange,
    operator,
    primaryCondition,
    secondaryCondition,
    setConfirmedOperator,
    setConfirmedPrimary,
    setConfirmedSecondary,
  ]);

  const handleReset = useCallback(() => {
    resetFilterData();
    if (!isBEGrid) {
      setConfirmedPrimary({ ...primaryCondition });
      setConfirmedSecondary({ ...secondaryCondition });
    }
    onModelChange(null);
    closeFilter?.();
  }, [
    closeFilter,
    isBEGrid,
    onModelChange,
    primaryCondition,
    resetFilterData,
    secondaryCondition,
    setConfirmedPrimary,
    setConfirmedSecondary,
  ]);

  return {
    operator,
    setOperator,
    closeFilter,
    primaryCondition,
    secondaryCondition,
    handleAccept,
    handleReset,
  };
};

export default useAgDateFilterCore;
